/** File a request: what kind it is, a title, and a markdown description. Opens the new request's page. */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FeedbackIn, feedbackInDescriptionMax, feedbackInTitleMax } from '@/api/generated/schemas';
import { useSubmitFeedback } from '@/api/queries';
import type { FeedbackCategory } from '@/api/types';
import { bannerError, fieldErrors, serverFieldErrors } from '@/api/validate';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { MarkdownEditor } from '@/ui/MarkdownEditor';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

import { AttachmentPicker, useAttachmentDraft } from './Attachments';
import { CATEGORIES } from './feedbackUi';

/** What a person types; the attachment ids come from the server. */
const TYPED = FeedbackIn.pick({ title: true, description: true });

const PLACEHOLDER: Record<FeedbackCategory, string> = {
  bug: 'What did you do, what did you expect, and what happened instead? Which page were you on?',
  enhancement: 'What works today, and how could it work better?',
  suggestion: 'What would you like to see, and what would it help you do?',
  question: 'What would you like to know?',
};

function CategoryPicker({ category, onChange }: { category: FeedbackCategory; onChange: (value: FeedbackCategory) => void }) {
  return (
    <fieldset className="field-group">
      <legend>Type</legend>
      <div className="feedback-types" role="radiogroup" aria-label="Type">
        {CATEGORIES.map(({ value, label, hint, Icon }) => (
          <label key={value} className={`feedback-type${category === value ? ' is-selected' : ''}`}>
            <input type="radio" name="feedback-category" value={value} checked={category === value} onChange={() => onChange(value)} />
            <Icon aria-hidden="true" />
            <span>
              <b>{label}</b>
              <span className="hint">{hint}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function FeedbackDialog({ onClose }: { onClose: () => void }) {
  const submit = useSubmitFeedback();
  const navigate = useNavigate();
  const [category, setCategory] = useState<FeedbackCategory>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const files = useAttachmentDraft();
  const invalid = Object.keys(fieldErrors(TYPED, { title, description })).length > 0;
  const server = serverFieldErrors(submit.error);

  return (
    <Modal
      title="New request"
      subtitle="Report a bug, propose an enhancement or a suggestion, or ask a question. You can follow it and comment on it here."
      size="wide"
      onClose={onClose}
      onSubmit={() =>
        submit.mutate(
          { category, title: title.trim(), description: description.trim(), attachment_ids: files.ids },
          {
            onSuccess: (item) => {
              files.sent();
              navigate(`/feedback/${item.id}`);
            },
          },
        )
      }
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <ActionButton state={buttonState(submit)} label="Send request" pendingLabel="Sending…" doneLabel="Sent" disabled={invalid || files.busy} />
        </>
      }
    >
      {/* A screenshot pasted anywhere in the form is attached; text pastes as usual. */}
      <div className="stack-md" onPaste={files.onPaste}>
        <FormError error={bannerError(submit.error, Object.keys(TYPED.shape))} />
        <CategoryPicker category={category} onChange={setCategory} />
        <div className="field">
          <label htmlFor="feedback-title">Title</label>
          <input
            id="feedback-title"
            type="text"
            required
            autoFocus
            maxLength={feedbackInTitleMax}
            value={title}
            placeholder="A short summary"
            {...fieldA11y('feedback-title', server.title)}
            onChange={(e) => setTitle(e.target.value)}
          />
          <FieldError inputId="feedback-title" message={server.title} />
        </div>
        <div className="field">
          <label htmlFor="feedback-description">Description</label>
          <MarkdownEditor
            id="feedback-description"
            value={description}
            onChange={setDescription}
            maxLength={feedbackInDescriptionMax}
            rows={8}
            placeholder={PLACEHOLDER[category]}
            error={server.description}
          />
        </div>
        <div className="field">
          <label htmlFor="feedback-files">Attachments</label>
          <AttachmentPicker draft={files} id="feedback-files" />
        </div>
      </div>
    </Modal>
  );
}
